/* RebootDeviceComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./RebootDeviceRenderer");
const ScheduleVeilComponent = require("../../../../lib/actions/veils/schedule/ScheduleVeilComponent");
class RebootDeviceComponent extends EventEmitter {
    constructor(
        logger,
        GameEditor,
        OsUpgradePlan,
        DeviceEntity,
        ActiveView,
        modalUserFeedback,
        userSession
    ) {
        super();
        this._logger = logger;
        this._gameEditor = GameEditor;
        this._osPlanEntity = OsUpgradePlan;
        this._deviceEntity = DeviceEntity;
        this._activeView = ActiveView;
        this._workId = undefined;
        this._modalUserFeedback = modalUserFeedback;

        this._userSession = userSession;
        this._renderer = new Renderer(this);
        this._registerListeners();
        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    get logger() {
        return this._logger;
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._renderer = undefined;
                this._workId = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "RebootDeviceComponent.shutdown() error.");
            }
        );
    }

    // prettier-ignore
    set "$teve.ui.context" (context) { //jshint ignore:line
        this._context = context;
    }
    cancelReboot() {
        this._context.reject({userRejected: true});
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    scheduleDevice(workInput) {
        let workTarget = this._getWorkTarget();
        let title = "Reboot Devices - Schedule";
        return this._modalUserFeedback
            .displayCustom(
                new ScheduleVeilComponent(this._logger, true, title, {
                    workType: "reboot",
                    firstTime: true,
                }),
                {
                    dontHide: false,
                }
            )
            .then((scheduleDef) => {
                let schedule = scheduleDef.schedule;
                let description = workInput.description;
                let workInputSchedule = Object.assign(workInput, scheduleDef.workInput);
                return this._deviceEntity.scheduleRebootWork(
                    this._userSession.activeOrganization.id,
                    description,
                    "",
                    schedule,
                    workTarget,
                    workInputSchedule
                );
            })
            .then(() => this._modalUserFeedback.hideDisplayed())
            .catch((err) => {
                if (!err.userRejected) {
                    if (this._logger) {
                        this._logger.warn(
                            {
                                error: err,
                            },
                            "RebootDeviceComponent._scheduleDevice() - Error"
                        );
                    } else {
                        console.error(err);
                    }
                    return this._modalUserFeedback.displayError(err);
                }
            });
    }

    startDevice(workInput) {
        let workTarget = this._getWorkTarget();
        /**
         *
         * @param {Object} progress - progress data object
         * @param {number} progress.startTime
         * @param {number} progress.targetCount
         * @param {number} progress.successCount
         * @param {number} progress.failureCount
         * @param {string} progress.deviceId
         * @param {string} progress.deviceName
         * @param {string} progress.deviceDescription
         */
        let resolveCancel;
        const cancelSignal = new Promise((r) => (resolveCancel = r));

        const progressCB = ({workId, successCount, failureCount, targetCount}) => {
            if (workId) {
                this._workId = workId;
            }
            const msg = `Succeeded ${successCount}, failed ${failureCount} of ${targetCount} Devices`;
            this._modalUserFeedback.showLoadingProgress(msg);
        };

        this._modalUserFeedback.displayLoading({
            message: "Rebooting your devices...",
            showAbortButton: true,
            abortLabel: "Cancel",
            onAbort: () =>
                resolveCancel({
                    errorCode: "UserCanceled",
                    errorMsg: "User has canceled the Reboot.",
                    title: "User Canceled",
                    error: true,
                }),
        });

        return Promise.race([
            this._deviceEntity.runRebootWork(
                this._userSession.activeOrganization.id,
                workTarget,
                workInput,
                {},
                progressCB
            ),
            cancelSignal,
        ])
            .then((result) => {
                if (result.errorCode === "UserCanceled") {
                    throw result;
                }
                if (result.errorMsg) {
                    let err = new Error(result.errorMsg);
                    err.errorCode = "RebootError";
                    err.errorMsg = result.errorMsg;
                    throw err;
                }
                let userNotificationMessage = "";
                if (!result.summary) {
                    userNotificationMessage = `Total number of devices: ${
                        result._trimmed.targetResults || 0
                    }`;
                } else {
                    const {targetCount, successCount, failureCount} = result.summary;
                    userNotificationMessage = [
                        `Total devices: ${targetCount || 0}`,
                        `Successful: ${successCount || 0}`,
                        `Errors: ${failureCount || 0}`,
                        "",
                    ].join("\r\n");
                }

                return this._modalUserFeedback.displayNotification(userNotificationMessage, {
                    title: "Reboot Completed",
                    confirmDetails: {
                        text: "OK",
                    },
                });
            })
            .catch((err) => {
                if (err.errorCode === "UserCancelled") {
                    this._cancelRebootWork();
                }

                const _logger = this._logger ? this._logger : this._activeView._logger;
                if (!err.userRejected) {
                    if (_logger) {
                        _logger.warn({error: err}, "RebootDeviceComponent._startDevice() - Error");
                    } else {
                        console.error(err);
                    }

                    this._modalUserFeedback.displayError(err);
                }
            });
    }

    _cancelRebootWork() {
        this._deviceEntity.cancelWork(this._userSession._activeOrganization.id, this._workId);
    }

    /**
     * Create workTarget Object from devices component selection
     * @param {String} selection
     * @returns {Object}
     */
    _getWorkTarget() {
        /*let sort = (this._activeView &&
            this._activeView.pagingFeed &&
            this._activeView.pagingFeed.getSort()) || { name: 1 };
        this._selectedDevices = {
            sort: sort,
            ids: this._activeView.getSelectedDeviceIds().selectedRows
        };*/
        let workTarget = {
            category: "entity",
            entityType: "Device",
            method: "multi",
            ids: this._activeView.getSelectedDeviceIds().selectedRows,
        };
        return workTarget;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = RebootDeviceComponent;
